import { api, unwrap, type APIResponse, type Paginated } from "./client";

// Mirrors app/routers/user.py UserRoleOut.
export interface UserRole {
  role_id: number;
  name: string;
}

// Mirrors app/routers/user.py UserOut.
export interface User {
  user_id: number;
  username: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  usr_id: number | null;
  active: boolean;
  roles: UserRole[];
}

export interface UserListParams {
  page?: number;
  per_page?: number;
  active_only?: boolean;
}

export async function listUsers(params: UserListParams): Promise<Paginated<User>> {
  const resp = await api.get<Paginated<User>>("/users/", { params });
  if (!resp.data.success) throw new Error(resp.data.error || "Failed to load users");
  return resp.data;
}

// Per-user feature flags (mirrors app/routers/user.py FeatureOut).
export interface UserFeature {
  permission: string;
  label: string;
  baseline: boolean;
  enabled: boolean;
}

export async function listUserFeatures(userId: number): Promise<UserFeature[]> {
  const resp = await api.get<APIResponse<UserFeature[]>>(`/users/${userId}/features`);
  return unwrap(resp.data);
}

export async function setUserFeatures(userId: number, permissions: string[]): Promise<void> {
  await api.put(`/users/${userId}/features`, { permissions });
}

export interface MyPermissions {
  is_admin: boolean;
  /** A floor-supervisor login: read-only, and closed to everything except live
   * queue status, recordings and stats. */
  is_restricted?: boolean;
  permissions: string[];
  // "flat" | "npa_nxx" — drives billing-mode-specific UI (e.g. the Rates page).
  billing_mode: string;
  // True when an admin opted this account into Authorize.Net card top-ups.
  card_payment_enabled?: boolean;
}

export async function getMyPermissions(): Promise<MyPermissions> {
  const resp = await api.get<APIResponse<MyPermissions>>("/users/me/permissions");
  return unwrap(resp.data);
}
